'use client'

import { useEffect, useState } from 'react'
import { CheckCircle2, Circle, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import type { PronunciationScript } from '@/content/pronunciation'
import {
  PRONUNCIATION_PROGRESS_CHANGED_EVENT,
  createDefaultPronunciationProgress,
  getWeakSoundTags,
  readPronunciationProgress,
  resetPronunciationProgress,
  writePronunciationProgress
} from '@/lib/pronunciation/progress'

type PronunciationProgressSummaryProps = {
  script: PronunciationScript
  lessonCount: number
}

export function PronunciationProgressSummary({
  script,
  lessonCount
}: PronunciationProgressSummaryProps) {
  const [progress, setProgress] = useState(createDefaultPronunciationProgress())
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    const refresh = () => {
      setProgress(readPronunciationProgress())
      setHydrated(true)
    }
    refresh()
    window.addEventListener(PRONUNCIATION_PROGRESS_CHANGED_EVENT, refresh)
    return () =>
      window.removeEventListener(PRONUNCIATION_PROGRESS_CHANGED_EVENT, refresh)
  }, [])

  const scriptProgress = progress.scripts[script]
  const completed = scriptProgress.completedLessons.length
  const attempts = Object.values(scriptProgress.lessonStats).reduce(
    (sum, stats) => sum + stats.attempts,
    0
  )
  const weakTags = getWeakSoundTags(progress, script, 5)
  const percent =
    lessonCount > 0 ? Math.round((completed / lessonCount) * 100) : 0

  const reset = () => {
    const next = resetPronunciationProgress(readPronunciationProgress(), script)
    writePronunciationProgress(next)
    setProgress(next)
  }

  return (
    <div className="flex flex-col justify-between gap-4 border-b pb-6 sm:flex-row sm:items-center">
      <div className="flex flex-1 flex-wrap items-center gap-x-6 gap-y-4">
        <div className="max-w-xs min-w-[200px] flex-1">
          <div className="text-muted-foreground mb-1.5 flex items-center justify-between text-xs font-semibold">
            <span>Progress</span>
            <span>
              {completed} of {lessonCount} lessons ({percent}%)
            </span>
          </div>
          <Progress value={percent} size="sm" />
        </div>
        <div className="border-muted/30 flex items-center gap-6 text-sm sm:border-l sm:pl-6">
          <div>
            <p className="text-muted-foreground text-xs font-medium">
              Practice rounds
            </p>
            <p className="text-foreground mt-0.5 font-semibold">{attempts}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs font-medium">
              Weak sounds
            </p>
            <p className="text-foreground mt-0.5 font-semibold">
              {weakTags.length}
            </p>
          </div>
        </div>
      </div>
      {hydrated && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={reset}
          className="self-start text-xs sm:self-auto"
        >
          <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
          Reset progress
        </Button>
      )}
    </div>
  )
}

export function PronunciationLessonStatus({
  script,
  lessonId
}: {
  script: PronunciationScript
  lessonId: string
}) {
  const [status, setStatus] = useState<'new' | 'in-progress' | 'done'>('new')

  useEffect(() => {
    const refresh = () => {
      const progress = readPronunciationProgress()
      const scriptProgress = progress.scripts[script]
      const completed = scriptProgress.completedLessons.includes(lessonId)
      if (completed) {
        setStatus('done')
      } else {
        const stats = scriptProgress.lessonStats[lessonId]
        const hasAttempts = stats && stats.attempts > 0
        setStatus(hasAttempts ? 'in-progress' : 'new')
      }
    }
    refresh()
    window.addEventListener(PRONUNCIATION_PROGRESS_CHANGED_EVENT, refresh)
    return () =>
      window.removeEventListener(PRONUNCIATION_PROGRESS_CHANGED_EVENT, refresh)
  }, [lessonId, script])

  if (status === 'done') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/5 px-2 py-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
        <CheckCircle2 className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
        Done
      </span>
    )
  }

  if (status === 'in-progress') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/5 px-2 py-1 text-[11px] font-medium text-amber-700 dark:text-amber-300">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" />
        In progress
      </span>
    )
  }

  return (
    <span className="text-muted-foreground border-muted/40 bg-muted/5 inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-medium">
      <Circle className="h-3 w-3" />
      New
    </span>
  )
}

function formatTag(tag: string) {
  return tag
    .replace(/^confusion-/, '')
    .replace(/^initial-/, '')
    .replace(/^final-/, '')
    .replace(/^tone-/, 'tone ')
    .replaceAll('-', ' / ')
}
